/**
 * ImportPreviewTable — color-coded preview of what /process produced.
 *
 * Replaces the earlier card-list <ReadyRowsPanel />. Rows are
 * color-coded:
 *   emerald = create  (new employee will be inserted)
 *   blue    = update  (existing employee, some fields changing)
 *   gray    = unchanged (existing employee, nothing to apply)
 *   rose    = error   (won't commit — listed in a separate section)
 *
 * Click any row → expand to see per-field diff (old → new) for
 * update rows, or the mapped data for create rows, plus the source
 * row from the original file.
 */

import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Plus,
  Minus,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "../../lib/api";

type _ListResult = Awaited<ReturnType<typeof api.importListRows>>;
type _Row = _ListResult["rows"][number];

type FilterKind = "all" | "create" | "update" | "unchanged" | "error";

export function ImportPreviewTable({ sessionId }: { sessionId: string }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FilterKind>("all");

  // Pull every row (cap 200 is the API ceiling for now — see rows.py).
  // The full count comes from `totals` which sums across all statuses
  // server-side, not just the loaded slice.
  const allQ = useQuery({
    queryKey: ["import-rows", sessionId, "all"],
    queryFn: () =>
      api.importListRows(sessionId, { status: "all", limit: 1000 }),
    refetchInterval: 5000,
  });

  const rows = allQ.data?.rows ?? [];
  const totals = allQ.data?.totals ?? {};

  // Counts per kind (computed from the loaded slice — totals object
  // from the API is keyed by status, not kind).
  const counts = useMemo(() => {
    let create = 0, update = 0, unchanged = 0, error = 0;
    for (const r of rows) {
      if (r.status === "error") error++;
      else if (r.update_kind === "update") update++;
      else if (r.update_kind === "unchanged") unchanged++;
      else create++;
    }
    return { create, update, unchanged, error };
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (filter === "all") return rows;
    if (filter === "error") return rows.filter((r) => r.status === "error");
    if (filter === "create")
      return rows.filter(
        (r) => r.status !== "error"
          && (r.update_kind === "create" || r.update_kind === null),
      );
    return rows.filter(
      (r) => r.status !== "error" && r.update_kind === filter,
    );
  }, [rows, filter]);

  if (allQ.isLoading) {
    return (
      <section className="rounded-xl border border-border-subtle bg-surface-default p-4">
        <p className="text-sm text-text-secondary">{t("app.ipt_loading")}</p>
      </section>
    );
  }
  if (rows.length === 0) return null;

  // Sum across all loaded statuses (valid + warning + error etc.)
  const totalLoaded = Object.values(totals).reduce(
    (a, b) => a + (b as number), 0,
  );

  return (
    <section className="rounded-xl border border-border-subtle bg-surface-default p-4 space-y-3">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold text-text-primary">
            {t("app.ipt_title")}
          </h3>
          <p className="text-xs text-text-secondary">
            {t(
              totalLoaded === 1 ? "app.ipt_subtitle_one" : "app.ipt_subtitle_many",
              { total: totalLoaded, shown: filteredRows.length, loaded: rows.length },
            )}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <_Pill
            label={t("app.ipt_pill_all", { n: rows.length })}
            tone="neutral"
            active={filter === "all"}
            onClick={() => setFilter("all")}
          />
          <_Pill
            label={t("app.ipt_pill_create", { n: counts.create })}
            tone="emerald"
            active={filter === "create"}
            onClick={() => setFilter("create")}
          />
          <_Pill
            label={t("app.ipt_pill_update", { n: counts.update })}
            tone="blue"
            active={filter === "update"}
            onClick={() => setFilter("update")}
          />
          <_Pill
            label={t("app.ipt_pill_unchanged", { n: counts.unchanged })}
            tone="gray"
            active={filter === "unchanged"}
            onClick={() => setFilter("unchanged")}
          />
          {counts.error > 0 && (
            <_Pill
              label={t("app.ipt_pill_error", { n: counts.error })}
              tone="rose"
              active={filter === "error"}
              onClick={() => setFilter("error")}
            />
          )}
        </div>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="border-b border-border-subtle text-xs uppercase tracking-wider text-text-tertiary">
            <tr>
              <th className="w-8"></th>
              <th className="px-2 py-1.5 text-start">{t("app.ipt_col_action")}</th>
              <th className="px-2 py-1.5 text-start">{t("app.ipt_col_name")}</th>
              <th className="px-2 py-1.5 text-start">{t("app.ipt_col_code")}</th>
              <th className="px-2 py-1.5 text-start">{t("app.ipt_col_hire")}</th>
              <th className="px-2 py-1.5 text-start">{t("app.ipt_col_nat")}</th>
              <th className="px-2 py-1.5 text-start">{t("app.ipt_col_changes")}</th>
              <th className="px-2 py-1.5 text-start">{t("app.ipt_col_source")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((r) => (
              <_TableRow key={r.id} row={r} />
            ))}
          </tbody>
        </table>
      </div>
      {rows.length === 1000 && (
        <p className="text-xs text-text-tertiary">
          {t("app.ipt_truncate")}
        </p>
      )}
    </section>
  );
}

function _TableRow({ row }: { row: _Row }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const mapped = row.mapped_data as Record<string, unknown>;

  const isError = row.status === "error";
  const isUpdate = row.update_kind === "update";
  const isUnchanged = row.update_kind === "unchanged";
  const hasWarning = row.status === "warning";

  // Row tint
  const tint = isError
    ? "bg-rose-50 hover:bg-rose-100/60"
    : isUpdate
      ? "bg-blue-50 hover:bg-blue-100/60"
      : isUnchanged
        ? "bg-neutral-50 hover:bg-neutral-100"
        : "bg-emerald-50 hover:bg-emerald-100/60";

  const action = isError
    ? t("app.ipt_action_error")
    : isUpdate
      ? t("app.ipt_action_update")
      : isUnchanged
        ? t("app.ipt_action_unchanged")
        : t("app.ipt_action_create");
  const actionIcon = isError
    ? <AlertCircle size={12} className="text-rose-700" />
    : isUpdate
      ? <Plus size={12} className="text-blue-700" />
      : isUnchanged
        ? <Minus size={12} className="text-text-tertiary" />
        : <Plus size={12} className="text-emerald-700" />;
  const actionColor = isError
    ? "text-rose-900"
    : isUpdate
      ? "text-blue-900"
      : isUnchanged
        ? "text-text-tertiary"
        : "text-emerald-900";

  const displayName = [
    mapped.first_name_en,
    mapped.middle_name_en,
    mapped.last_name_en,
  ].filter(Boolean).join(" ") || "—";
  const employeeCode = String(mapped.employee_code ?? "—");
  const hireDate = String(mapped.hire_date ?? "—").slice(0, 10);
  const nat = String(mapped.nationality ?? "—");
  const isSaudi = mapped.is_saudi_national === true;
  const changesCount = Object.keys(row.field_changes ?? {}).length;

  return (
    <>
      <tr
        className={`border-b border-border-subtle/50 cursor-pointer transition-colors ${tint}`}
        onClick={() => setOpen((o) => !o)}
      >
        <td className="px-1 py-1.5">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </td>
        <td className={`px-2 py-1.5 ${actionColor} text-xs font-medium`}>
          <span className="inline-flex items-center gap-1">
            {actionIcon}
            {action}
            {hasWarning && !isError && (
              <AlertTriangle
                size={11}
                className="text-amber-600"
                aria-label={t("app.ipt_warnings_aria")}
              />
            )}
          </span>
        </td>
        <td className="px-2 py-1.5 font-medium text-text-primary">
          {displayName}
        </td>
        <td className="px-2 py-1.5 text-xs text-text-secondary">
          {employeeCode}
        </td>
        <td className="px-2 py-1.5 text-xs text-text-tertiary">{hireDate}</td>
        <td className="px-2 py-1.5 text-xs text-text-tertiary">
          {nat} {isSaudi ? "🇸🇦" : ""}
        </td>
        <td className="px-2 py-1.5 text-xs text-text-tertiary">
          {isUpdate
            ? t(
                changesCount === 1 ? "app.ipt_field_one" : "app.ipt_field_many",
                { n: changesCount },
              )
            : "—"}
        </td>
        <td className="px-2 py-1.5 text-xs text-text-tertiary">
          {t("app.ipt_source_row", {
            sheet: row.source_sheet_name,
            row: row.source_row_index + 2,
          })}
        </td>
      </tr>
      {open && (
        <tr className={tint}>
          <td colSpan={8} className="px-4 py-3 border-b border-border-subtle/50">
            <_RowDetail row={row} isUpdate={isUpdate} />
          </td>
        </tr>
      )}
    </>
  );
}

function _RowDetail({ row, isUpdate }: { row: _Row; isUpdate: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      {isUpdate && Object.keys(row.field_changes).length > 0 && (
        <div>
          <p className="text-xs font-medium text-blue-900 mb-1.5">
            {t("app.ipt_fields_changing")}
          </p>
          <table className="text-xs">
            <thead className="text-text-tertiary">
              <tr>
                <th className="px-2 py-1 text-start">{t("app.ipt_dcol_field")}</th>
                <th className="px-2 py-1 text-start">{t("app.ipt_dcol_current")}</th>
                <th className="px-2 py-1 text-start">{t("app.ipt_dcol_new")}</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(row.field_changes).map(([field, diff]) => (
                <tr key={field} className="border-t border-blue-200/40">
                  <td className="px-2 py-0.5 font-mono text-text-secondary">
                    {field}
                  </td>
                  <td className="px-2 py-0.5 text-text-tertiary">
                    {diff.old ?? t("app.ipt_empty")}
                  </td>
                  <td className="px-2 py-0.5 text-blue-900 font-medium">
                    {diff.new}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {row.validation_messages.length > 0 && (
        <div>
          <p className="text-xs font-medium text-amber-900 mb-1">
            {t("app.ipt_warnings_h")}
          </p>
          <ul className="space-y-0.5">
            {row.validation_messages.map((m, i) => (
              <li
                key={i}
                className={
                  m.level === "error"
                    ? "text-xs text-rose-800"
                    : "text-xs text-amber-800"
                }
              >
                {m.field ? <strong>{m.field}: </strong> : null}
                {m.message_en}
              </li>
            ))}
          </ul>
        </div>
      )}
      <details>
        <summary className="cursor-pointer text-xs font-medium text-text-secondary">
          {t("app.ipt_mapped_data")}
        </summary>
        <pre className="mt-1 overflow-x-auto rounded bg-neutral-100 p-2 text-xs">
          {JSON.stringify(row.mapped_data, null, 2)}
        </pre>
      </details>
      <details>
        <summary className="cursor-pointer text-xs font-medium text-text-secondary">
          {t("app.ipt_source_orig")}
        </summary>
        <pre className="mt-1 overflow-x-auto rounded bg-neutral-100 p-2 text-xs">
          {JSON.stringify(row.source_data, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function _Pill({
  label,
  tone,
  active,
  onClick,
}: {
  label: string;
  tone: "neutral" | "emerald" | "blue" | "gray" | "rose";
  active: boolean;
  onClick: () => void;
}) {
  const tones = {
    neutral: active
      ? "bg-brand-blue text-white"
      : "bg-neutral-100 text-text-secondary hover:bg-neutral-200",
    emerald: active
      ? "bg-emerald-700 text-white"
      : "bg-emerald-50 text-emerald-800 hover:bg-emerald-100",
    blue: active
      ? "bg-blue-600 text-white"
      : "bg-blue-50 text-blue-800 hover:bg-blue-100",
    gray: active
      ? "bg-neutral-600 text-white"
      : "bg-neutral-100 text-text-secondary hover:bg-neutral-200",
    rose: active
      ? "bg-rose-600 text-white"
      : "bg-rose-50 text-rose-800 hover:bg-rose-100",
  } as const;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${tones[tone]}`}
    >
      {label}
    </button>
  );
}
